

import { Button, Alert, Combobox, FormField, TextInput, NumberInput, ToggleInput, SuccessNotice, useToast } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SubscriptionPlanType } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateSubscriptionPlanMutation } from "../services/useCreateSubscriptionPlanMutation";

type Props = {
  onSuccess?: () => void;
};

export function SubscriptionPlanCreateForm({ onSuccess }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planType, setPlanType] = useState<SubscriptionPlanType>(
    SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION
  );
  const [amountValue, setAmountValue] = useState("0");
  const [lastPaidAmount, setLastPaidAmount] = useState("0");
  const isFreeTrial = planType === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL;
  const { mutateAsync: createPlan } = useCreateSubscriptionPlanMutation();

  const handleTypeChange = (nextType: SubscriptionPlanType) => {
    setPlanType(nextType);

    if (nextType === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL) {
      if (amountValue.trim() && amountValue !== "0") {
        setLastPaidAmount(amountValue);
      }
      setAmountValue("0");
      return;
    }

    setAmountValue(lastPaidAmount);
  };

  const handleAmountChange = (value: string) => {
    setAmountValue(value);
    if (!isFreeTrial) {
      setLastPaidAmount(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);
    const form = event.currentTarget;
    const formData = new FormData(form);
    try {
      await createPlan({
        nameAr: String(formData.get("nameAr") ?? ""),
        nameEn: String(formData.get("nameEn") ?? ""),
        type: planType,
        amount: Number(formData.get("amount")),
        periodDays: Number(formData.get("periodDays")),
        isActive: formData.get("isActive") === "on",
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    form.reset();
    setPlanType(SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION);
    setAmountValue("0");
    setLastPaidAmount("0");
    setSuccess(true);
    toast.success(t(keys.common.updateSuccess));
    setIsSubmitting(false);
    onSuccess?.();
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}
      <FormField label={t(keys.admin.subscriptionPlanNameAr)} name="nameAr" required>
        <TextInput name="nameAr" required placeholder={t(keys.placeholders.name)} />
      </FormField>
      <FormField label={t(keys.admin.subscriptionPlanNameEn)} name="nameEn">
        <TextInput name="nameEn" placeholder={t(keys.placeholders.name)} />
      </FormField>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label={t(keys.admin.subscriptionPlanType)} name="type" required>
          <Combobox
            value={planType}
            onChange={(value) => {
              if (value) handleTypeChange(value as SubscriptionPlanType);
            }}
            options={[
              {
                label: t(keys.admin.subscriptionPlanTypePaid),
                value: SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION,
              },
              {
                label: t(keys.admin.subscriptionPlanTypeFreeTrial),
                value: SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL,
              },
            ]}
            aria-label={t(keys.admin.subscriptionPlanType)}
          />
        </FormField>
        <FormField
          label={t(keys.admin.subscriptionPlanAmount)}
          name="amount"
          required
          helperText={
            isFreeTrial ? t(keys.admin.subscriptionPlanAmountFreeTrialHelper) : undefined
          }
        >
          <NumberInput
            name="amount"
            placeholder="0"
            required
            allowDecimal
            value={amountValue}
            onChange={(event) => handleAmountChange(event.target.value)}
            disabled={isFreeTrial}
          />
        </FormField>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label={t(keys.admin.subscriptionPlanPeriodDays)} name="periodDays" required>
          <NumberInput name="periodDays" placeholder="30" required />
        </FormField>
        <FormField label={t(keys.admin.subscriptionPlanIsActive)} name="isActive">
          <ToggleInput name="isActive" defaultChecked />
        </FormField>
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary" className="h-11 rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {isSubmitting ? t(keys.common.loading) : t(keys.common.create)}
      </Button>
    </form>
  );
}
